import { useCallback, useEffect, useState } from "react";
import { API } from "@/lib/api";

// Client-side view of the server-enforced site gate (the server is what protects data).
// Needed in production builds, where the dev-server gate (siteGate.js) does not run.
export const SiteGateBoundary = ({ children }) => {
  const [state, setState] = useState("checking");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    try {
      const r = await fetch(`${API}/gate/status`, { credentials: "include", cache: "no-store" });
      const d = await r.json();
      setState(d.unlocked ? "open" : "locked");
    } catch {
      setState("locked");
    }
  }, []);

  useEffect(() => {
    check();
    const onLocked = () => setState("locked");
    window.addEventListener("wua:site-locked", onLocked);
    return () => window.removeEventListener("wua:site-locked", onLocked);
  }, [check]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const r = await fetch(`${API}/gate/login`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password: pw }),
      });
      if (r.ok) { window.location.reload(); return; }
      const d = await r.json().catch(() => ({}));
      setErr(r.status === 429 ? `Too many attempts. Please try again in ${Math.ceil((d.retry_after || 900) / 60)} minutes.`
        : r.status === 401 ? "Incorrect password." : "Preview access is unavailable right now.");
    } catch {
      setErr("Could not reach the server. Please try again.");
    }
    setPw("");
    setBusy(false);
  };

  if (state === "open") return children;
  if (state === "checking") return <div data-testid="site-gate-checking" className="min-h-screen bg-[#f6f3ec]" />;
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f6f3ec] p-6 text-[#001a47]">
      <main data-testid="site-gate" className="w-full max-w-[400px] bg-white border border-[#e4dfd3] border-t-4 border-t-[#001a47] px-8 py-10 shadow-[0_20px_50px_-30px_rgba(0,26,71,.45)]">
        <img src="/logo.png" alt="WhichUniversityAdvisor" className="h-10 mb-7" />
        <h1 className="font-serif text-[28px] leading-tight mb-2">Private preview</h1>
        <p className="text-sm text-slate-600 mb-7">WhichUniversityAdvisor is not public yet. Enter the preview password to continue.</p>
        <form onSubmit={submit} autoComplete="off">
          <label htmlFor="site-gate-pw" className="block text-xs font-semibold tracking-widest uppercase mb-2">Preview password</label>
          <input id="site-gate-pw" type="password" required autoFocus value={pw} onChange={(e) => setPw(e.target.value)}
            data-testid="site-gate-password" className="w-full px-3.5 py-3 border border-slate-300 text-base outline-none focus:border-[#001a47] transition-colors" />
          <button type="submit" disabled={busy} data-testid="site-gate-submit"
            className="mt-4 w-full py-3 bg-[#001a47] hover:bg-[#00296b] text-white font-semibold transition-colors disabled:opacity-60">Enter</button>
          <div role="alert" data-testid="site-gate-error" className="min-h-5 mt-3 text-[13px] text-red-700">{err}</div>
        </form>
        <div className="mt-7 text-xs text-slate-400">Access lasts 7 days on this browser.</div>
      </main>
    </div>
  );
};
